/**
 * PrimarySaleChart Component
 *
 * A TypeScript component that displays primary sale ticket availability data over time
 * using a bar chart visualization. This component fetches data from the GetPrimarySaleData
 * API endpoint and renders it using the RCBarChart component.
 *
 * @module PrimarySaleChart
 */

import React, { useEffect, useState } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import {
    Box,
    Typography,
    LinearProgress,
    Alert,
    Card,
    CardContent,
    IconButton,
    Tooltip,
    Chip
} from '@mui/material';
import {
    Refresh,
    Fullscreen,
    FullscreenExit,
    TrendingDown
} from '@mui/icons-material';
import RCBarChart from '../../../components/charts/recharts/rcBarChart';

/**
 * Props interface for the PrimarySaleChart component
 */
interface PrimarySaleChartProps {
    /** The site identifier to fetch primary sale data for */
    siteId: string;
    /** The market type to filter primary sale data */
    marketType: string;
    /** Optional width for the chart (defaults to 1100) */
    width?: number;
    /** Optional height for the chart (defaults to 500) */
    height?: number;
}

/**
 * Daily data structure from the API response
 */
interface PrimarySaleDailyData {
    /** Date in ISO 8601 format */
    date: string;
    /** Number of documents found for this date */
    documentCount: number;
    /** Maximum available count for this date */
    maxAvailableCount: number | null;
}

/**
 * API response structure from GetPrimarySaleData endpoint
 */
interface PrimarySaleDataResult {
    /** Indicates if the query executed successfully */
    success: boolean;
    /** The site identifier that was queried */
    siteId: string;
    /** Start date of the query range in ISO 8601 format */
    startDate: string;
    /** End date of the query range in ISO 8601 format */
    endDate: string;
    /** Array of daily aggregated primary sale data */
    dailyData: PrimarySaleDailyData[];
}

/**
 * Transformed data structure for RCBarChart
 */
interface ChartDataPoint {
    /** Formatted date string for display (e.g., '30th') */
    date: string;
    /** Month name for display (e.g., 'July') */
    month: string;
    /** Ticket availability count */
    availability: number;
    /** Full date for tooltip */
    fullDate: string;
}

/**
 * Gets ordinal suffix for day (st, nd, rd, th)
 *
 * @param {number} day - Day of the month
 * @returns {string} Ordinal suffix
 */
const getOrdinalSuffix = (day: number): string => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
    }
};

/**
 * Formats an ISO date string to day with ordinal suffix for chart display
 *
 * @param {string} isoDateString - ISO 8601 formatted date string
 * @returns {string} Formatted date string like '30th', '7th', '14th'
 */
const formatDate = (isoDateString: string): string => {
    try {
        const date = new Date(isoDateString);
        const day = date.getDate();
        return `${day}${getOrdinalSuffix(day)}`;
    } catch (_error) {
        return isoDateString;
    }
};

/**
 * Formats an ISO date string to month name
 *
 * @param {string} isoDateString - ISO 8601 formatted date string
 * @returns {string} Month name like 'July', 'August'
 */
const formatMonth = (isoDateString: string): string => {
    try {
        const date = new Date(isoDateString);
        return date.toLocaleDateString('en-US', { month: 'long' });
    } catch (_error) {
        return '';
    }
};

/**
 * Formats an ISO date string to full date format for tooltip
 *
 * @param {string} isoDateString - ISO 8601 formatted date string
 * @returns {string} Formatted date string in YYYY-MM-DD format
 */
const formatFullDate = (isoDateString: string): string => {
    try {
        const date = new Date(isoDateString);
        return date.toISOString().split('T')[0];
    } catch (_error) {
        return isoDateString;
    }
};

/**
 * Custom X-Axis Tick Component to display individual days and month changes
 */
const CustomXAxisTick = (props: any) => {
    const { x, y, payload, chartData, showLabelEvery } = props;
    const data = payload?.value;
    const dataArray = chartData || [];
    // Find the current data point based on fullDate
    const currentData = dataArray.find((d: ChartDataPoint) => d.fullDate === data);
    if (!currentData) return null;

    // Find the actual index in the full chartData array
    const fullIndex = dataArray.findIndex((item: ChartDataPoint) => item.fullDate === currentData.fullDate);
    
    // We're rendering every day's tick (interval = 0). Use the immediate previous
    // data item to detect month boundaries so month labels appear when the month
    // actually changes (or on the very first tick).
    const previousItem = fullIndex > 0 ? dataArray[fullIndex - 1] : null;
    const showMonth = fullIndex === 0 || (previousItem && previousItem.month !== currentData.month);
    const step = showLabelEvery && Number.isInteger(showLabelEvery) && showLabelEvery > 0 ? showLabelEvery : 1;
    const showByStep = fullIndex % step === 0;
    const shouldShowLabel = showMonth || showByStep;
    if (!shouldShowLabel) return null;
        
    return (
        <g transform={`translate(${x},${y})`}>
            {/* Individual day */}
            <text
                x={0}
                y={0}
                dy={8}
                textAnchor="middle"
                fill="#666"
                fontSize={10}
                fontWeight={500}
            >
                {formatDate(currentData.fullDate)}
            </text>
            {/* Month label - only when month changes */}
            {showMonth && (
                <text
                    x={0}
                    y={0}
                    dy={24}
                    textAnchor="middle"
                    fill="#888"
                    fontSize={10}
                    fontWeight={400}
                >
                    ({currentData.month})
                </text>
            )}
        </g>
    );
};

/**
 * Custom Tooltip Component to display full date and proper field name
 */
const CustomTooltip = (props: any) => {
    const { active, payload } = props;
    
    if (active && payload && payload.length > 0) {
        const data = payload[0].payload;
        return (
            <Box sx={{
                backgroundColor: '#333',
                border: '1px solid #333',
                borderRadius: 1,
                p: 2,
                color: '#fff',
                fontSize: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
            }}>
                <Typography variant="body2" sx={{ mb: 1, color: '#fff', fontWeight: 600 }}>
                    {data.fullDate}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                        width: 12,
                        height: 12,
                        backgroundColor: '#4db6ac',
                        borderRadius: '50%'
                    }} />
                    <Typography variant="body2" sx={{ color: '#fff', fontSize: '12px' }}>
                        Maximum of
                    </Typography>
                    {/* Display the current value to float right */}
                    <Typography variant="body2" sx={{ color: '#fff', fontSize: '12px', fontWeight: 600, ml: 1, flexGrow: 1, textAlign: 'right' }}>
                        {payload[0].value}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Typography variant="body2" sx={{ color: '#fff', fontSize: '12px' }}>
                        Primary.CurrentAvailableCount
                    </Typography>
                </Box>
            </Box>
        );
    }
    
    return null;
};

/**
 * Transforms API response data into the format expected by RCBarChart
 *
 * Converts the daily data array from the API response into an array of objects
 * with 'date' and 'availability' properties suitable for bar chart rendering.
 * Filters out entries with null or zero availability counts.
 *
 * @param {PrimarySaleDailyData[]} dailyData - Array of daily data from API response
 * @returns {ChartDataPoint[]} Array of transformed data points for the chart
 */
const transformDataForChart = (dailyData: PrimarySaleDailyData[]): ChartDataPoint[] => {
    if (!dailyData || dailyData.length === 0) {
        return [];
    }

    return dailyData
        // Show sold out days as zero availability, but skip nulls
        .filter(item => item.maxAvailableCount !== null && item.maxAvailableCount >= 0)
        .map(item => ({
            date: formatDate(item.date),
            month: formatMonth(item.date),
            availability: item.maxAvailableCount as number,
            fullDate: formatFullDate(item.date)
        }));
};



/**
 * PrimarySaleChart Component
 *
 * Displays a bar chart showing primary sale ticket availability over time.
 * Fetches data from the GetPrimarySaleData API endpoint and handles loading,
 * error, and empty states appropriately.
 *
 * @param {PrimarySaleChartProps} props - Component props
 * @returns {JSX.Element} Rendered chart component with loading/error/empty states
 */
const PrimarySaleChart: React.FC<PrimarySaleChartProps> = ({
    siteId,
    marketType,
    width: _width = 1200,
    height = 400
}) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
    const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

    /**
     * Fetches primary sale data from the API
     *
     * Makes a GET request to the RTP_Event/GetPrimarySaleData endpoint with the
     * provided siteId parameter. Transforms the response data and updates component state.
     * Handles errors by displaying user-friendly notifications and error states.
     *
     * @async
     * @returns {Promise<void>}
     */
    const fetchPrimarySaleData = async (): Promise<void> => {
        if (!siteId || !marketType) {
            setError('Site ID and Market Type are required');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await dataProvider.fetchData(
                'RTP_Event',
                `GetPrimarySaleData?siteId=${encodeURIComponent(siteId)}&marketType=${encodeURIComponent(marketType)}`,
            );

            if (response && response.data) {
                const result = response.data as PrimarySaleDataResult;
                
                if (result.success && result.dailyData) {
                    const transformedData = transformDataForChart(result.dailyData);
                    setChartData(transformedData);
                    
                    if (transformedData.length === 0) {
                        setError('No availability data found for this event');
                    }
                } else {
                    setError('Failed to retrieve primary sale data');
                }
            } else {
                setError('Invalid response from server');
            }
        } catch (err: unknown) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
            setError(`Failed to load primary sale data: ${errorMessage}`);
            notify(`Error loading primary sale chart: ${errorMessage}`, {
                type: 'error',
                autoHideDuration: 5000
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchPrimarySaleData();
    }, [siteId, dataProvider, notify]);

    const handleRefresh = () => {
        fetchPrimarySaleData();
    };

    const toggleFullscreen = () => {
        setIsFullscreen(!isFullscreen);
    };

    // Calculate stats
    const maxValue = chartData.length > 0 ? Math.max(...chartData.map(d => d.availability)) : 0;
    const totalDays = chartData.length;
    const currentValue = chartData.length > 0 ? chartData[chartData.length - 1].availability : 0;

    if (isLoading) {
        return (
            <Card elevation={1} sx={{ mt: 2, mb: 2 }}>
                <CardContent sx={{ p: 3 }}>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Loading primary sale data...
                    </Typography>
                    <LinearProgress
                        color="primary"
                        sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: '#4db6ac'
                            }
                        }}
                    />
                </CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Card elevation={1} sx={{ mt: 2, mb: 2 }}>
                <CardContent sx={{ p: 3 }}>
                    <Alert
                        severity="error"
                        action={
                            <IconButton color="inherit" size="small" onClick={handleRefresh}>
                                <Refresh />
                            </IconButton>
                        }
                    >
                        {error}
                    </Alert>
                </CardContent>
            </Card>
        );
    }

    if (chartData.length === 0) {
        return (
            <Card elevation={1} sx={{ mt: 2, mb: 2 }}>
                <CardContent sx={{ p: 3 }}>
                    <Alert severity="info">
                        No primary sale availability data is available for this event.
                    </Alert>
                </CardContent>
            </Card>
        );
    }

    // Make chart width proportional to number of days so bars stay close together.
    // Avoid forcing a large minimum width (the previous Math.max(width, ...) made small datasets spread out).
    const perDay = 12; // pixels allocated per day (smaller => tighter packing)
    const minWidth = 300;
    // Keep per-day spacing identical in fullscreen: base width purely on data length
    // so bars don't stretch to fill window width when fullscreen.
    const chartWidth = Math.max(minWidth, chartData.length * perDay);
    // Increase non-fullscreen height slightly so bars have more vertical room
    const chartHeight = isFullscreen ? window.innerHeight - 200 : height + 120;

    return (
        <Card
            elevation={1}
            sx={{
                mt: 2,
                mb: 2,
                border: '1px solid #e0e0e0',
                ...(isFullscreen && {
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 9999,
                    overflow: 'auto'
                })
            }}
        >
            <CardContent sx={{ p: isFullscreen ? 4 : 3 }}>
                {/* Header */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3
                }}>
                    <Box>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                mb: 1
                            }}
                        >
                            {/* Add icon trend */}
                            <TrendingDown fontSize="small" sx={{ mr: 1 }} />
                            Primary Sale Availability Trend
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Chip
                                label={`${totalDays} days`}
                                size="small"
                                sx={{
                                    backgroundColor: '#e8f5e8',
                                    color: '#2e7d32',
                                    fontSize: '12px'
                                }}
                            />
                            <Chip
                                label={`Peak: ${maxValue.toLocaleString()}`}
                                size="small"
                                sx={{
                                    backgroundColor: '#e3f2fd',
                                    color: '#1976d2',
                                    fontSize: '12px'
                                }}
                            />
                            <Chip
                                label={`Current: ${currentValue.toLocaleString()}`}
                                size="small"
                                sx={{
                                    backgroundColor: '#fff3e0',
                                    color: '#ef6c00',
                                    fontSize: '12px'
                                }}
                            />
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title='Refresh Data'>
                            <IconButton
                                onClick={handleRefresh}
                                size="small"
                                disabled={isLoading}
                            >
                                <Refresh fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
                            <IconButton
                                onClick={toggleFullscreen}
                                size="small"
                            >
                                {isFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Chart Container */}
                <Box sx={{
                    borderRadius: 2,
                    p: 2,
                    backgroundColor: '#fff',
                    border: '1px solid #e0e0e0',
                    overflowX: 'auto',
                    overflowY: 'hidden'
                }}>
                    <RCBarChart
                        width={chartWidth}
                        height={chartHeight}
                        data={chartData}
                        hideGrid={false}
                        customTooltip={CustomTooltip}
                        forceTheme="light"
                        xAxis={{
                            dataKey: 'fullDate',
                            tick: (props: any) => <CustomXAxisTick {...props} chartData={chartData} showLabelEvery={7} />,
                            height: 50,
                            interval: 0 // show every day's tick
                        }}
                        yAxis={{
                            position: 'insideLeft',
                            label: {
                                value: 'Maximum of Primary.CurrentAvailableCount',
                                angle: -90,
                                dx: -10,
                                style: { fill: '#666', fontSize: 12, fontWeight: 'bold' }
                            },
                            tick: { fontSize: 11, fill: '#666' },
                            domain: [0, Math.ceil(maxValue / 100) * 100],
                            ticks: Array.from({ length: Math.ceil(maxValue / 100) + 1 }, (_, i) => i * 100),
                            interval: 0
                        }}
                        seriesList={[
                            {
                                dataKey: 'availability',
                                name: 'Primary.Timestamp per day',
                                fill: '#4db6ac',
                                stroke: '#4db6ac',
                                strokeWidth: 0
                            }
                        ]}
                        // Pack bars adjacent: no gap and size tuned to per-day allocation
                        barGap={0}
                        barSize={Math.max(6, Math.floor(perDay * 0.6))}
                        legendStyle={{
                            fontSize: '12px',
                            fontWeight: 'bold',
                            paddingTop: '0px',
                            marginTop: '0px'
                        }}
                    />
                </Box>
            </CardContent>
        </Card>
    );
};

export default PrimarySaleChart;
